import React, {
  useContext, useEffect, useState,
} from 'react';
import { ReactSVG } from 'react-svg';
import { FormattedMessage, useIntl } from 'react-intl';
import useAccountProjects from '../../hooks/useAccountProjects';

import Loader from '../../components/Loader';
import RequestModal from '../../components/RequestModal/RequestModal';
import ProjectCard from './ProjectCard';
import Filter from './Filter';
import Sorting from './Sorting';
import exclamationMarkAlt from '../../assets/image/map/exclamation-mark-alt.svg';
import './ExistingProject.scss';

import { appStore } from '../../state/app';

const PAGE_SIZE = 20;

const getPaginationItems = (totalPages, currentPage) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 2) {
    return [1, 2, 3, '...', totalPages];
  }

  const items = [];
  const left = currentPage - 1;
  const right = currentPage + 1;

  items.push(1);

  if (left > 2) {
    items.push('...');
  }

  const start = Math.max(2, left);
  const end = right >= totalPages - 2 ? totalPages - 1 : Math.min(totalPages - 1, right);
  for (let page = start; page <= end; page += 1) {
    items.push(page);
  }

  if (right < totalPages - 2) {
    items.push('...');
  }

  items.push(totalPages);

  return items;
};

const ExistingProject = () => {
  const translate = useIntl();
  const { state } = useContext(appStore);
  const accountId = state?.account?.accountId || '';
  const [showRequestModal, setShowRequestModal] = useState(false);
  const defaultSort = { key: 'status.keyword', value: 'asc' };
  const [activeSort, setActiveSort] = useState(defaultSort);
  const [filterQuery, setFilterQuery] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [requestedPage, setRequestedPage] = useState(1);
  const [isPaging, setIsPaging] = useState(false);
  const [displayPage, setDisplayPage] = useState(1);
  const {
    isLoading,
    isError,
    projectsList,
    totalCount,
    refreshList,
  } = useAccountProjects(accountId, filterQuery, activeSort, requestedPage, PAGE_SIZE);
  const totalPages = Math.ceil(totalCount / PAGE_SIZE) || 0;
  const paginationItems = totalPages ? getPaginationItems(totalPages, displayPage) : [];

  useEffect(() => {
    if (isError) {
      setShowRequestModal(true);
    }
  }, [isError]);

  useEffect(() => {
    setCurrentPage(1);
    setRequestedPage(1);
    setDisplayPage(1);
  }, [filterQuery, activeSort]);

  const handleSortChange = (nextSort) => {
    setActiveSort(nextSort);
  };

  const handleResetFilters = () => {
    setActiveSort(defaultSort);
  };

  useEffect(() => {
    if (totalPages && currentPage > totalPages) {
      setCurrentPage(totalPages);
      setRequestedPage(totalPages);
    }
    if (totalPages && requestedPage > totalPages) {
      setRequestedPage(totalPages);
    }
    if (totalPages && displayPage > totalPages) {
      setDisplayPage(totalPages);
    }
  }, [totalPages, currentPage, requestedPage, displayPage]);

  useEffect(() => {
    if (!isLoading) {
      setDisplayPage(requestedPage);
      setIsPaging(false);
    }
  }, [isLoading, requestedPage]);
  return (
    <>
      {showRequestModal && (
        <RequestModal
          error
          handleClose={() => setShowRequestModal(false)}
          title="Oops!"
          description="Something goes wrong!"
        />
      )}
      <h2 className="existing-projects__title">{translate.formatMessage({ id: 'existing.title' })}</h2>
      <div className="existing-projects__filter">
        <Filter setFilterQuery={setFilterQuery} onReset={handleResetFilters} />
      </div>
      <div className="existing-projects__sorting">
        <Sorting
          activeSort={activeSort}
          setActiveSort={handleSortChange}
        />
      </div>
      {isLoading ? (
        <div className="existing-projects__loader">
          <Loader />
        </div>
      ) : isError ? (
        <div className="existing-projects__loader">
          {translate.formatMessage({ id: 'existing.simpleError' })}
        </div>
      ) : projectsList.length ? (
        <>
          <div className="existing-projects__list">
            {projectsList.map((data) => (
              <ProjectCard
                data={data}
                refreshList={refreshList}
                key={data._id}
              />
            ))}
          </div>
          {totalPages > 1 && (
            <div className={`existing-projects__pagination${isLoading || isPaging ? ' is-loading' : ''}`}>
              {paginationItems.map((item, index) => (
                item === '...'
                  ? (
                    // eslint-disable-next-line react/no-array-index-key
                    <span className="existing-projects__pagination-ellipsis" key={`ellipsis-${index}`}>
                      ...
                    </span>
                  )
                  : (
                    <button
                      type="button"
                      className={`existing-projects__pagination-item${item === displayPage ? ' is-active' : ''}`}
                      onClick={() => {
                        if (item === currentPage || isLoading || isPaging) return;
                        setIsPaging(true);
                        setCurrentPage(item);
                        setRequestedPage(item);
                      }}
                      key={item}
                      disabled={isLoading || isPaging}
                      aria-current={item === displayPage ? 'page' : undefined}
                    >
                      {item}
                    </button>
                  )
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="existing-projects__empty">
          <ReactSVG src={exclamationMarkAlt} alt="Exclamation" />
          <FormattedMessage
            id="existing.emptyList"
            values={{
              div: (...chunks) => <div className="existing-projects__empty-description">{chunks}</div>,
              bold: (chunks) => <b>{chunks}</b>,
              br: <br />,
            }}
          />
        </div>
      )}
    </>
  );
};

export default ExistingProject;
